import type { UseManageMeI } from "@/hooks/use-manage-me";

import {
  WHATS_NEW_AUDIENCES,
  WhatsNewAudienceT,
  WhatsNewCardI,
  WhatsNewEntryI,
} from "./types";

type ManageViewerI = Pick<
  UseManageMeI,
  "status" | "isAdmin" | "isLga" | "isPodLead" | "impersonating"
>;

/**
 * The viewer's audience (C16-D313): the strongest role wins, Admin > LGA >
 * Pod Lead > Caller. Null means "don't announce on this visit": an Admin is
 * impersonating someone (a support session isn't the audience), or
 * manage/me is still loading or failed. With roles switched off everyone is
 * a caller. `isImpersonatedSession` is the session's own signal (the
 * Glencoco-signed token), since manage/me can't say so while roles are off.
 */
export const getWhatsNewAudience = (
  manage: ManageViewerI,
  isImpersonatedSession: boolean
): WhatsNewAudienceT | null => {
  if (isImpersonatedSession) {
    return null;
  }

  if (manage.status === "loading" || manage.status === "error") {
    return null;
  }

  if (manage.status === "off") {
    return WHATS_NEW_AUDIENCES.CALLER;
  }

  if (manage.impersonating) {
    return null;
  }

  if (manage.isAdmin) {
    return WHATS_NEW_AUDIENCES.ADMIN;
  }

  if (manage.isLga) {
    return WHATS_NEW_AUDIENCES.LGA;
  }

  if (manage.isPodLead) {
    return WHATS_NEW_AUDIENCES.POD_LEAD;
  }

  return WHATS_NEW_AUDIENCES.CALLER;
};

/** A static entry as one audience sees it; null when it has nothing for them. */
export const resolveWhatsNewEntry = (
  entry: WhatsNewEntryI,
  audience: WhatsNewAudienceT
): WhatsNewCardI | null => {
  const title = entry.title[audience];
  const sections = entry.sections
    .filter((section) => section.audiences.includes(audience))
    .map(({ audiences: _audiences, ...section }) => section);

  if (!title || sections.length === 0) {
    return null;
  }

  return {
    id: entry.id,
    startsAt: entry.startsAt,
    endsAt: entry.endsAt,
    title,
    intro: entry.intro?.[audience],
    sections,
    ...(entry.maxDaysPerUser !== undefined && {
      maxDaysPerUser: entry.maxDaysPerUser,
    }),
  };
};
